import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import useAxiosInstance from "../../../../../Utilities/Hooks/useAxiosInstance";

import {
  BUNDLE_OFFERS_API,
  PRODUCTS_API,
  CATAGORY_API,
  BRANDS_API,
  PRODUCT_COLLECTIONS_API,
} from "../../../../../Utilities/APIs/APIs";

import Form from "../../../../Partials/Layouts/Forms/Form";
import ShortTextInput from "../../../../Partials/Layouts/Forms/FormInputs/ShortTextInput/ShortTextInput";
import FormSubmitButton from "../../../../Partials/Layouts/Forms/FormSubmitButton/FormSubmitButton";
import DateInput from "../../../../Partials/Layouts/Forms/FormInputs/DateInput/DateInput";
import SelectInput from "../../../../Partials/Layouts/Forms/FormInputs/SelectInput/SelectInput";
import SelectOption from "../../../../Partials/Layouts/Forms/FormInputs/SelectInput/SelectOption/SelectOption";

import BundleTargetPicker from "./BundleTargetPicker";
import "./bundleOffer.css";

function CreateBundleOffer({
  triggerFetch,
  setShowCreateForm,
}) {
  const axiosInstance = useAxiosInstance();

  const [name, setName] = useState("");
  const [appliesTo, setAppliesTo] =useState("products");
  const [products, setProducts] = useState([]);
  const [categories, setCategories] =useState([]);
  const [brands, setBrands] = useState([]);
  const [collections, setCollections] =useState([]);
  const [productGroups, setProductGroups] =useState([]);
  const [requiredQuantity, setRequiredQuantity] =useState(2);
  const [discountValue, setDiscountValue] =useState(50);
  const [customerLabel, setCustomerLabel] = useState("");
  const [offerLink, setOfferLink] = useState("");
  const [startDate, setStartDate] =useState("");
  const [endDate, setEndDate] =useState("");
  const [isActive, setIsActive] =useState(true);
  const [productOptions, setProductOptions] =useState([]);
  const [categoryOptions, setCategoryOptions] =useState([]);
  const [brandOptions, setBrandOptions] =useState([]);
  const [discountType, setDiscountType] = useState("amount");

  const [
    collectionOptions,
    setCollectionOptions,
  ] = useState([]);

  const [
    productGroupOptions,
    setProductGroupOptions,
  ] = useState([]);

  const [error, setError] = useState("");
  const [submitting, setSubmitting] =
    useState(false);

  // ==========================================
  // LOAD OPTIONS
  // ==========================================

  useEffect(() => {
    async function loadOptions() {
      try {
        const [
          productRes,
          categoryRes,
          brandRes,
          collectionRes,
        ] = await Promise.all([
          axiosInstance.get(
            `${PRODUCTS_API}?page=1&limit=1000`
          ),

          axiosInstance.get(
            `${CATAGORY_API}?page=1&limit=1000`
          ),

          axiosInstance.get(
            `${BRANDS_API}?page=1&limit=1000`
          ),

          axiosInstance.get(
            `${PRODUCT_COLLECTIONS_API}?page=1&limit=1000`
          ),
        ]);

        // PRODUCTS
        const productData =
          productRes?.data?.products ||
          productRes?.data ||
          [];

        setProductOptions(
          productData.map((product) => ({
            value: product._id,
            label:
              product.name ||
              product.squId ||
              "Unnamed Product",
          }))
        );

        // Product Groups
        const uniqueGroups = [
          ...new Set(
            productData
              .map(
                (product) =>
                  product.productGroup
              )
              .filter(Boolean)
          ),
        ];

        setProductGroupOptions(
          uniqueGroups.map((group) => ({
            value: group,
            label: group,
          }))
        );

        // CATEGORIES
        const categoryData =
          categoryRes?.data?.categories ||
          categoryRes?.data ||
          [];

        setCategoryOptions(
          categoryData.map((category) => ({
            value: category._id,
            label:
              category.categoryName ||
              category.name ||
              "Unnamed Category",
          }))
        );

        // BRANDS
        const brandData =
          brandRes?.data?.brands ||
          brandRes?.data ||
          [];

        setBrandOptions(
          brandData.map((brand) => ({
            value: brand._id,
            label:
              brand.name ||
              "Unnamed Brand",
          }))
        );

        // COLLECTIONS
        const collectionData =
          collectionRes?.data
            ?.productCollections ||
          collectionRes?.data?.collections ||
          collectionRes?.data ||
          [];

        setCollectionOptions(
          collectionData.map((collection) => ({
            value: collection._id,
            label:
              collection.name ||
              "Unnamed Collection",
          }))
        );
      } catch (error) {
        console.error(
          "Failed to load bundle options:",
          error
        );
      }
    }

    loadOptions();
  }, [axiosInstance]);

  // ==========================================
  // RESET IRRELEVANT SELECTIONS
  // ==========================================

  useEffect(() => {
    if (appliesTo !== "products") {
      setProducts([]);
    }

    if (appliesTo !== "category") {
      setCategories([]);
    }

    if (appliesTo !== "brand") {
      setBrands([]);
    }

    if (appliesTo !== "collection") {
      setCollections([]);
    }

    if (appliesTo !== "productGroup") {
      setProductGroups([]);
    }
  }, [appliesTo]);

  // ==========================================
  // PREVIEW
  // ==========================================

  const preview = useMemo(() => {
    const qty =
      Number(requiredQuantity) || 0;

    const discount =
      Number(discountValue) || 0;

    if (qty <= 0 || discount <= 0) {
      return null;
    }

    return {
      firstQty: qty,
      firstDiscount: discount,

      secondQty: qty * 2,
      secondDiscount: discount * 2,

      thirdQty: qty * 3,
      thirdDiscount: discount * 3,
    };
  }, [requiredQuantity, discountValue]);

  // ==========================================
  // VALIDATION
  // ==========================================

  const hasTargetSelection = () => {
    if (appliesTo === "all") return true;

    if (appliesTo === "products") {
      return products.length > 0;
    }

    if (appliesTo === "category") {
      return categories.length > 0;
    }

    if (appliesTo === "brand") {
      return brands.length > 0;
    }

    if (appliesTo === "collection") {
      return collections.length > 0;
    }

    if (appliesTo === "productGroup") {
      return productGroups.length > 0;
    }

    if (!discountType) {
      setError(
        "Please select a discount type."
      );
      return;
    }

    if (
      discountType === "percentage" &&
      Number(discountValue) > 100
    ) {
      setError(
        "Percentage cannot be more than 100%."
      );
      return;
    }

    return false;
  };

  async function handleSubmit(e) {
    e.preventDefault();

    setError("");

    if (!name.trim()) {
      setError(
        "Please enter an offer name."
      );
      return;
    }

    if (!hasTargetSelection()) {
      setError(
        "Please select at least one eligible item."
      );
      return;
    }

    if (
      Number(requiredQuantity) < 1
    ) {
      setError(
        "Required quantity must be at least 1."
      );
      return;
    }

    if (Number(discountValue) <= 0) {
      setError(
        "Discount amount must be greater than 0."
      );
      return;
    }

    if (!startDate || !endDate) {
      setError(
        "Please select start and end dates."
      );
      return;
    }

    if (
      new Date(endDate) <
      new Date(startDate)
    ) {
      setError(
        "End date cannot be before start date."
      );
      return;
    }

    const itemData = {
      name: name.trim(),
      appliesTo,
      products,
      categories,
      brands,
      collections,
      productGroups,
      requiredQuantity: Number(requiredQuantity),
      discountType,
      discountValue: Number(discountValue),
      customerLabel,
      offerLink,
      startDate,
      endDate,
      isActive,
    };

    try {
      setSubmitting(true);

      const response =
        await axiosInstance.post(
          BUNDLE_OFFERS_API,
          itemData
        );

      if (response) {
        triggerFetch();
        setShowCreateForm(false);
      }
    } catch (error) {
      console.error(error);

      setError(
        error?.response?.data?.message ||
          "Failed to create bundle offer."
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
  <div className="bundle-offer-form">
    <Form onSubmit={handleSubmit}>
      <div>

        <ShortTextInput
          label="Offer Name"
          value={name}
          placeholder="e.g. Buy 2 Tees Get ৳50 Off"
          setState={setName}
        />

        <div className="bundle-help-text">
          Internal name used to identify this bundle offer.
        </div>


        <div style={{ marginTop: "18px" }}>
          <SelectInput
            label="Applies To"
            value={appliesTo}
            setState={setAppliesTo}
          >
            <SelectOption
              optionValue="products"
              optionText="Selected Products"
            />

            <SelectOption
              optionValue="category"
              optionText="Selected Categories"
            />

            <SelectOption
              optionValue="brand"
              optionText="Selected Brands"
            />

            <SelectOption
              optionValue="collection"
              optionText="Selected Collections"
            />

            <SelectOption
              optionValue="productGroup"
              optionText="Product Groups"
            />

            <SelectOption
              optionValue="all"
              optionText="All Products"
            />
          </SelectInput>
        </div>


        {appliesTo === "products" && (
          <BundleTargetPicker
            label="Eligible Products"
            options={productOptions}
            selected={products}
            setSelected={setProducts}
          />
        )}


        {appliesTo === "category" && (
          <BundleTargetPicker
            label="Eligible Categories"
            options={categoryOptions}
            selected={categories}
            setSelected={setCategories}
          />
        )}


        {appliesTo === "brand" && (
          <BundleTargetPicker
            label="Eligible Brands"
            options={brandOptions}
            selected={brands}
            setSelected={setBrands}
          />
        )}


        {appliesTo === "collection" && (
          <BundleTargetPicker
            label="Eligible Collections"
            options={collectionOptions}
            selected={collections}
            setSelected={setCollections}
          />
        )}


        {appliesTo === "productGroup" && (
          <BundleTargetPicker
            label="Eligible Product Groups"
            options={productGroupOptions}
            selected={productGroups}
            setSelected={setProductGroups}
          />
        )}


        {appliesTo === "all" && (
          <div className="bundle-info-box">
            Every non-discounted product will count
            toward this bundle offer.
          </div>
        )}


        <ShortTextInput
          label="Buy Quantity"
          value={requiredQuantity}
          placeholder="e.g. 2"
          setState={setRequiredQuantity}
        />

        <div className="bundle-help-text">
          Number of eligible products required for one
          bundle.
        </div>

        <SelectInput
          label="Discount Type"
          value={discountType}
          setState={setDiscountType}
        >
          <SelectOption
            optionValue="amount"
            optionText="Fixed Amount (৳)"
          />

          <SelectOption
            optionValue="percentage"
            optionText="Percentage (%)"
          />
        </SelectInput>

        <ShortTextInput
          label={
            discountType === "percentage"
              ? "Discount Percentage (%)"
              : "Discount Amount (৳)"
          }
          value={discountValue}
          placeholder={
            discountType === "percentage"
              ? "e.g. 5"
              : "e.g. 50"
          }
          setState={setDiscountValue}
        />

        <div className="bundle-help-text">
          Discount is repeated for every complete bundle.
        </div>


        {preview && (
          <div className="bundle-preview">

            <div className="bundle-preview-title">
              Offer Preview
            </div>

            {discountType === "amount" ? (
              <>
                <div className="bundle-preview-primary">
                  Buy {preview.firstQty} → Get ৳
                  {preview.firstDiscount} Off
                </div>

                <div className="bundle-preview-secondary">
                  Buy {preview.secondQty} → ৳
                  {preview.secondDiscount} off
                  <br />

                  Buy {preview.thirdQty} → ৳
                  {preview.thirdDiscount} off
                </div>
              </>
            ) : (
              <>
                <div className="bundle-preview-primary">
                  Buy {requiredQuantity} → Get{" "}
                  {discountValue}% Off
                </div>

                <div className="bundle-preview-secondary">
                  {discountValue}% will be applied to
                  eligible products once the required
                  quantity is reached.
                </div>
              </>
            )}

            <div className="bundle-preview-note">
              Already discounted products are automatically
              excluded.
            </div>

          </div>
        )}

         <ShortTextInput
          label="Label for Customers (Optional)"
          value={customerLabel}
          placeholder="Boxy Tank"
          setState={setCustomerLabel}
        />

         <ShortTextInput
          label="Link"
          value={offerLink}
          placeholder="www.stridesco.com/offer"
          setState={setOfferLink}
        />

        <DateInput
          label="Start Date"
          value={startDate}
          placeholder="Start Date"
          setState={setStartDate}
        />

        <div style={{ marginTop: "18px" }}>
          <DateInput
            label="End Date"
            value={endDate}
            placeholder="End Date"
            setState={setEndDate}
          />
        </div>


        <label className="bundle-active-row">
          <input
            type="checkbox"
            checked={isActive}
            onChange={(e) =>
              setIsActive(e.target.checked)
            }
          />

          <span>
            Activate this offer
          </span>
        </label>


        {error && (
          <p className="bundle-error">
            {error}
          </p>
        )}

      </div>

      <FormSubmitButton
        text={
          submitting
            ? "Creating..."
            : "Create Bundle Offer"
        }
      />
    </Form>
  </div>
);
}

export default CreateBundleOffer;